import React, { useState } from 'react';
import Button from '../Button';
import ConfirmDialog from '../ConfirmDialog';
import EmptyState from '../EmptyState';
import ReadFailed from '../ReadFailed';
import { READING_WORDING as RW, reading } from '../../domain/reading';
import PackageTemplateDrawer from './PackageTemplateDrawer';
import { usePackageTemplates } from '../../data/usePackageTemplates';
import { useMoneyFormat, useCurrency } from '../../context/LocaleContext';
import {
  BLANK_TEMPLATE, formatDuration, formatExpiry, templateFormErrors, templateFormOf,
  templateSaveInput,
} from '../../domain/packageTemplates';
import { templatePriceMinor } from '../../domain/packageSale';
import s from './packages.module.css';
import t from '../SimpleTable.module.css';

// `Packages → Templates`.
//
// MMS's list columns, in their order: `Name | Duration | Expiration |
// Description | Price`, with a `⋮` per row offering `Edit` and `Delete`. A
// blank description renders `-` there and here.
//
// The `⋮` is two buttons instead, which is ours; the actions and their order
// are MMS's.
//
// Delete is soft: MMS's confirm says "If this package template has been used,
// it will be marked as 'Inactive'.", so the confirmation here quotes the
// consequence. That it is always soft is ours (`usePackageTemplates.jsx`).

export default function PackageTemplatesPanel() {
  const {
    templates, loaded, failed, saveTemplate, removeTemplate,
  } = usePackageTemplates();
  const formatMoney = useMoneyFormat();
  const currency = useCurrency();

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_TEMPLATE);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const startAdd = () => {
    setEditingId(null);
    setForm(BLANK_TEMPLATE());
    setOpen(true);
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    // The stored template back into the boxes a teacher typed — the duration
    // split, the minor units as a decimal through the core's `toDecimal` — is
    // `templateFormOf` (src/domain/packageTemplates.js).
    setForm(templateFormOf(row, currency));
    setOpen(true);
  };

  const save = async () => {
    // Refused here as well as shown under the boxes: the drawer's Save is not
    // disabled (see its header), so this is the thing that actually stops a
    // bad write rather than a greyed button that explains nothing.
    if (templateFormErrors(form, currency).refused) return;
    setSaving(true);
    try {
      await saveTemplate({ id: editingId, ...templateSaveInput(form, currency) });
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  // `ConfirmDialog` rather than `window.confirm`, which is that component's own
  // rule: a native dialog blocks the event loop and cannot carry a third
  // option. Two buttons here — the destructive one in the discard slot and
  // `Cancel` beside it — because there is no third answer to give: this dialog
  // is not guarding unsaved edits, so "don't save" and "cancel" are one thing.
  const remove = async () => {
    const row = deleting;
    setDeleting(null);
    if (row) await removeTemplate(row.id);
  };

  return (
    <>
      {/* The toolbar and empty state the other tab pages use (#570): the add
          action on the left of a `.bar`, as Package Balances, Invoices and
          Family Accounts put theirs, and the empty state inside the table's
          border. */}
      <div className={s.bar}>
        <Button onClick={startAdd}>+ Package Template</Button>
      </div>

      {templates.length === 0 && !loaded && failed ? (
        <ReadFailed />
      ) : templates.length === 0 && !loaded ? (
        <div className={s.empty}>Reading the studio’s package templates…</div>
      ) : templates.length === 0 ? (
        <div className={t.wrap}>
          <EmptyState
            reading={reading(loaded, templates.length, failed)}
            title={RW['packages.templatesEmptyTitle']}
            description={RW['packages.templatesEmptyDescription']}
            actionLabel="+ Package Template"
            onAction={startAdd}
          />
        </div>
      ) : (
        <div className={t.wrap}>
          <table className={t.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Duration</th>
                <th>Expiration</th>
                <th>Description</th>
                <th>Price</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {templates.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>{formatDuration(row.minutes)}</td>
                  <td>{formatExpiry(row.expiryDays)}</td>
                  <td className={row.description ? undefined : t.muted}>
                    {row.description || '-'}
                  </td>
                  <td>{formatMoney(templatePriceMinor(row))}</td>
                  <td>
                    <div className={s.rowActions}>
                      <button type="button" className={t.linkButton} onClick={() => startEdit(row)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className={t.linkButton}
                        onClick={() => setDeleting(row)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PackageTemplateDrawer
        open={open}
        form={form}
        editing={Boolean(editingId)}
        saving={saving}
        onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
        onClose={() => setOpen(false)}
        onSave={save}
      />

      {/* MMS's own words, quoted: "If this package template has been used, it
          will be marked as 'Inactive'." The second sentence is ours, and is
          what a teacher worries about: deleting a template leaves the balances
          and ledger rows of its sales exactly as they were. */}
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete Package Template"
        message={(
          <>
            If this package template has been used, it will be marked as
            {' '}<b>Inactive</b>. Packages already sold from it keep the name, price and
            duration they were sold at, and are not affected.
          </>
        )}
        discardLabel="Delete"
        onDiscard={remove}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
